import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  User,
  Phone,
  Building2,
  FileText,
  UserPlus,
  Check,
  Eye,
  EyeOff,
  ArrowRight,
  Package,
  Truck,
  BadgePercent,
  MapPin,
  Store,
  ExternalLink,
  Home,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useUserAuth } from "../context/UserAuthContext";
import { cn } from "@/lib/utils";
import { AuthSidePromoSlot } from "../components/auth/AuthSidePromoSlot";
import { AuthPanelCornerDecor } from "../components/auth/AuthPanelCornerDecor";
import { AuthMobileHeroAccents } from "../components/auth/AuthMobileHeroAccents";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_V2 = `${API}/api/v2`;
const ANNUAIRE_ENTREPRISES = "https://annuaire-entreprises.data.gouv.fr/";

const PANEL_BULLETS = [
  { icon: BadgePercent, text: "Tarifs et offres selon votre profil" },
  { icon: Package, text: "Suivi des commandes en ligne" },
  { icon: Truck, text: "Retrait en agence ou livraison" },
];

function passwordChecks(pw) {
  return {
    len: pw.length >= 8 && pw.length <= 128,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    num: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

function postalDigits(s) {
  return String(s || "").replace(/\D/g, "").slice(0, 5);
}

function phoneOk(s) {
  const d = String(s || "").replace(/\D/g, "");
  return d.length >= 8;
}

function Stepper({ current, steps }) {
  return (
    <nav aria-label="Étapes d'inscription" className="mb-8">
      <ol className="flex items-center gap-2">
        {steps.map((s, idx) => {
          const done = current > s.id;
          const active = current === s.id;
          return (
            <li key={s.id} className="flex flex-1 items-center gap-2 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <span
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors",
                    done && "bg-emerald-500 text-white shadow-sm",
                    active && !done && "bg-[#190276] text-white shadow-md shadow-[#190276]/25",
                    !active && !done && "border-2 border-zinc-300 bg-white text-zinc-400",
                  )}
                >
                  {done ? <Check className="h-4 w-4" strokeWidth={2.5} /> : s.id}
                </span>
                <span
                  className={cn(
                    "hidden text-center text-[11px] font-semibold uppercase tracking-wide sm:block",
                    active ? "text-[#190276]" : "text-zinc-400",
                  )}
                >
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={cn(
                    "mb-5 hidden h-0.5 flex-1 rounded-full sm:block",
                    current > s.id ? "bg-emerald-400" : "bg-zinc-200",
                  )}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

async function fetchBan(q) {
  const r = await fetch(`${API_V2}/public/address-search?q=${encodeURIComponent(q)}`);
  if (!r.ok) return [];
  const d = await r.json();
  return Array.isArray(d.suggestions) ? d.suggestions : [];
}

export default function InscriptionPage() {
  const { register, isAuthenticated } = useUserAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [isPro, setIsPro] = useState(false);
  const prevPro = useRef(isPro);
  const [civility, setCivility] = useState("mr");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    billing_street: "",
    billing_complement: "",
    billing_postal_code: "",
    billing_city: "",
    favorite_store_id: "",
    company_name: "",
    siret: "",
    vat_number: "",
  });

  /* Pro — étape 1 : contexte géographique + requête entreprise */
  const [proCompanyQuery, setProCompanyQuery] = useState("");
  const [proLocSearch, setProLocSearch] = useState("");
  const [proLocOpen, setProLocOpen] = useState(false);
  const [proLocSuggestions, setProLocSuggestions] = useState([]);
  const [proGeoPostal, setProGeoPostal] = useState("");
  const [proGeoCity, setProGeoCity] = useState("");
  const [refLat, setRefLat] = useState(null);
  const [refLng, setRefLng] = useState(null);

  /* Pro — étape 2 */
  const [proSiretLocked, setProSiretLocked] = useState(true);
  const [companyResults, setCompanyResults] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [proCompanyPicked, setProCompanyPicked] = useState(false);

  /* Adresse facturation (particulier ét.2 / pro ét.3) */
  const [billAddrSearch, setBillAddrSearch] = useState("");
  const [billAddrOpen, setBillAddrOpen] = useState(false);
  const [billAddrSuggestions, setBillAddrSuggestions] = useState([]);

  /* Agence : affinage lieu (pro ét.4 / particulier ét.3) */
  const [agencySearch, setAgencySearch] = useState("");
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [agencySuggestions, setAgencySuggestions] = useState([]);

  const [storesList, setStoresList] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  /** Remplace le formulaire d'inscription pro (même écran) : SIRET déjà enregistré après validation. */
  const [proSiretBlocked, setProSiretBlocked] = useState(false);

  const steps = useMemo(() => {
    if (isPro) {
      return [
        { id: 1, label: "Compte" },
        { id: 2, label: "Entreprise" },
        { id: 3, label: "Facturation" },
        { id: 4, label: "Agence" },
      ];
    }
    return [
      { id: 1, label: "Compte" },
      { id: 2, label: "Coordonnées" },
      { id: 3, label: "Agence" },
      { id: 4, label: "Validation" },
    ];
  }, [isPro]);

  const agencyStep = isPro ? 4 : 3;
  const billingStepPro = 3;
  const billingStepPerso = 2;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (prevPro.current !== isPro) {
      prevPro.current = isPro;
      setStep(1);
      setProCompanyPicked(false);
      setCompanyResults([]);
      setProSiretLocked(true);
      setProSiretBlocked(false);
    }
  }, [isPro]);

  useEffect(() => {
    const raw = searchParams.get("email");
    if (!raw) return;
    try {
      const decoded = decodeURIComponent(raw).trim();
      if (decoded) setForm((f) => ({ ...f, email: decoded }));
    } catch {
      const t = raw.trim();
      if (t) setForm((f) => ({ ...f, email: t }));
    }
  }, [searchParams]);

  const emailFromFlow = Boolean(searchParams.get("email")?.trim());

  useEffect(() => {
    if (isAuthenticated) navigate("/compte", { replace: true });
  }, [isAuthenticated, navigate]);

  const checks = useMemo(() => passwordChecks(form.password), [form.password]);

  /* BAN — localisation pro étape 1 */
  useEffect(() => {
    if (step !== 1 || !isPro) {
      setProLocSuggestions([]);
      return;
    }
    const q = proLocSearch.trim();
    if (q.length < 3) {
      setProLocSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setProLocSuggestions(await fetchBan(q));
      } catch {
        setProLocSuggestions([]);
      }
    }, 380);
    return () => clearTimeout(t);
  }, [proLocSearch, step, isPro]);

  /* BAN — facturation */
  useEffect(() => {
    const active =
      (step === billingStepPerso && !isPro) || (step === billingStepPro && isPro);
    if (!active) {
      setBillAddrSuggestions([]);
      return;
    }
    const q = billAddrSearch.trim();
    if (q.length < 3) {
      setBillAddrSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setBillAddrSuggestions(await fetchBan(q));
      } catch {
        setBillAddrSuggestions([]);
      }
    }, 380);
    return () => clearTimeout(t);
  }, [billAddrSearch, step, isPro, billingStepPerso, billingStepPro]);

  /* BAN — recherche agence */
  useEffect(() => {
    if (step !== agencyStep) {
      setAgencySuggestions([]);
      return;
    }
    const q = agencySearch.trim();
    if (q.length < 3) {
      setAgencySuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setAgencySuggestions(await fetchBan(q));
      } catch {
        setAgencySuggestions([]);
      }
    }, 380);
    return () => clearTimeout(t);
  }, [agencySearch, step, agencyStep]);

  /* Recherche entreprise — pro étape 2 */
  useEffect(() => {
    if (step !== 2 || !isPro) {
      setCompanyResults([]);
      return;
    }
    const q = proCompanyQuery.trim();
    if (q.length < 2) {
      setCompanyResults([]);
      return;
    }
    const pc = postalDigits(proGeoPostal);
    const qDigits = q.replace(/\D/g, "");
    const t = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const params = new URLSearchParams({ q });
        // SIRET 14 chiffres : le backend n'applique pas le filtre CP (évite liste vide si CP ≠ siège).
        if (qDigits.length !== 14 && pc.length >= 2) params.set("postal_code", pc);
        const r = await fetch(`${API_V2}/public/company-search?${params}`);
        if (!r.ok) {
          setCompanyResults([]);
          if (r.status === 404) {
            toast.error(
              "Route introuvable : vérifiez REACT_APP_BACKEND_URL et que l’API expose /api/v2/public/company-search.",
            );
          } else if (r.status === 429) toast.error("Trop de recherches. Patientez un instant.");
          else if (r.status >= 500) toast.error("Service entreprises temporairement indisponible.");
          return;
        }
        const d = await r.json();
        setCompanyResults(Array.isArray(d.results) ? d.results : []);
      } catch {
        setCompanyResults([]);
        toast.error("Impossible de joindre le serveur pour la recherche entreprise.");
      } finally {
        setCompanyLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [step, isPro, proCompanyQuery, proGeoPostal]);

  /* Liste magasins */
  useEffect(() => {
    if (step !== agencyStep) return;
    let cancelled = false;
    setStoresLoading(true);
    (async () => {
      try {
        let list = [];
        if (refLat != null && refLng != null) {
          const r = await fetch(
            `${API_V2}/stores/nearest?lat=${encodeURIComponent(refLat)}&lng=${encodeURIComponent(refLng)}&limit=25`,
          );
          if (r.ok) {
            list = await r.json();
          }
        }
        if (!cancelled && (!list || list.length === 0)) {
          const pc = postalDigits(form.billing_postal_code);
          const url = pc
            ? `${API_V2}/stores?postal_code=${encodeURIComponent(pc)}`
            : `${API_V2}/stores`;
          const r2 = await fetch(url);
          if (r2.ok) list = await r2.json();
        }
        if (!cancelled) setStoresList(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setStoresList([]);
      } finally {
        if (!cancelled) setStoresLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, agencyStep, refLat, refLng, form.billing_postal_code]);

  const step1ValidBase =
    form.email.trim() &&
    form.password.length >= 8 &&
    (form.first_name || "").trim() &&
    (form.last_name || "").trim() &&
    phoneOk(form.phone) &&
    acceptCgv;

  const step1ValidPro =
    step1ValidBase &&
    proCompanyQuery.trim().length >= 2 &&
    postalDigits(proGeoPostal).length >= 4;

  const step1Valid = isPro ? step1ValidPro : step1ValidBase;

  const step2ProValid =
    proCompanyPicked &&
    (form.company_name || "").trim() &&
    (form.siret || "").replace(/\D/g, "").length === 14;

  const step2PersoValid =
    (form.billing_street || "").trim() &&
    postalDigits(form.billing_postal_code).length >= 4 &&
    (form.billing_city || "").trim();

  const step3ProValid =
    (form.billing_street || "").trim() &&
    postalDigits(form.billing_postal_code).length >= 4 &&
    (form.billing_city || "").trim();

  const storeSelected = Boolean((form.favorite_store_id || "").trim());

  const siretDigits = (form.siret || "").replace(/\D/g, "");
  const selectedSiretAlreadyRegistered = useMemo(() => {
    if (siretDigits.length !== 14) return false;
    return companyResults.some(
      (x) => (x.siret || "").replace(/\D/g, "") === siretDigits && x.already_registered,
    );
  }, [siretDigits, companyResults]);
  const submitProOk =
    isPro &&
    siretDigits.length === 14 &&
    (form.company_name || "").trim() &&
    storeSelected &&
    !companyResults.some(
      (x) => (x.siret || "").replace(/\D/g, "") === siretDigits && x.already_registered,
    );

  const submitPersoOk = !isPro && storeSelected;

  const applyGeoFromSuggestion = (s) => {
    const lat = s.latitude != null ? Number(s.latitude) : null;
    const lng = s.longitude != null ? Number(s.longitude) : null;
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      setRefLat(lat);
      setRefLng(lng);
    }
  };

  const pickProLocation = (s) => {
    setProGeoPostal(s.postcode || "");
    setProGeoCity(s.city || "");
    setProLocSearch(s.label || `${s.postcode} — ${s.city}`);
    applyGeoFromSuggestion(s);
    setProLocOpen(false);
    setProLocSuggestions([]);
  };

  const pickBillingAddress = (s) => {
    setForm((f) => ({
      ...f,
      billing_street: s.street_line || s.label,
      billing_postal_code: s.postcode || f.billing_postal_code,
      billing_city: s.city || f.billing_city,
    }));
    applyGeoFromSuggestion(s);
    setBillAddrOpen(false);
    setBillAddrSuggestions([]);
    setBillAddrSearch("");
  };

  const pickAgencyLocation = (s) => {
    applyGeoFromSuggestion(s);
    const pc = s.postcode || "";
    if (pc) {
      setForm((f) => ({ ...f, billing_postal_code: f.billing_postal_code || pc }));
    }
    setAgencySearch(s.label || "");
    setAgencyOpen(false);
    setAgencySuggestions([]);
  };

  const pickCompany = (row) => {
    const street = (row.address || "").split("\n")[0].trim() || row.address || "";
    setForm((f) => ({
      ...f,
      company_name: row.name,
      siret: row.siret,
      billing_street: street,
      billing_postal_code: row.postal_code || f.billing_postal_code,
      billing_city: row.city || f.billing_city,
    }));
    setProCompanyPicked(true);
    setProSiretLocked(true);
    toast.success("Entreprise sélectionnée", { description: row.siret_display || row.siret });
  };

  const goNext = useCallback(
    (from) => {
      if (from === 1) {
        if (!step1Valid) {
          if (!acceptCgv) toast.error("Veuillez accepter les conditions générales de vente.");
          else if (!phoneOk(form.phone)) toast.error("Indiquez un numéro de téléphone valide.");
          else if (isPro && proCompanyQuery.trim().length < 2)
            toast.error("Indiquez un SIRET ou une raison sociale.");
          else if (isPro && postalDigits(proGeoPostal).length < 4)
            toast.error("Indiquez un code postal (ou choisissez une suggestion).");
          else toast.error("Complétez les champs obligatoires.");
          return;
        }
        setStep(2);
      } else if (from === 2) {
        if (isPro) {
          if (!step2ProValid) {
            toast.error("Sélectionnez votre entreprise dans la liste.");
            return;
          }
          if (selectedSiretAlreadyRegistered) {
            setProSiretBlocked(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          setStep(3);
        } else {
          if (!step2PersoValid) {
            toast.error("Renseignez l'adresse, le code postal et la ville.");
            return;
          }
          setStep(3);
        }
      } else if (from === 3) {
        if (isPro) {
          if (!step3ProValid) {
            toast.error("Complétez les détails de facturation.");
            return;
          }
          setStep(4);
        } else {
          if (!storeSelected) {
            toast.error("Choisissez une agence.");
            return;
          }
          setStep(4);
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [
      step1Valid,
      step2ProValid,
      step2PersoValid,
      step3ProValid,
      storeSelected,
      acceptCgv,
      form.phone,
      isPro,
      proCompanyQuery,
      proGeoPostal,
      selectedSiretAlreadyRegistered,
    ],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPro && !submitProOk) {
      toast.error("Vérifiez l'agence sélectionnée et les informations entreprise.");
      return;
    }
    if (!isPro && !submitPersoOk) {
      toast.error("Choisissez une agence.");
      return;
    }
    setLoading(true);
    try {
      await register({
        email: form.email.trim(),
        password: form.password,
        first_name: (form.first_name || "").trim(),
        last_name: (form.last_name || "").trim(),
        phone: (form.phone || "").trim(),
        billing_street: (form.billing_street || "").trim(),
        billing_complement: (form.billing_complement || "").trim(),
        billing_postal_code: (form.billing_postal_code || "").trim(),
        billing_city: (form.billing_city || "").trim(),
        favorite_store_id: (form.favorite_store_id || "").trim(),
        company_name: isPro ? (form.company_name || "").trim() || undefined : undefined,
        siret: isPro ? (form.siret || "").trim() || undefined : undefined,
        vat_number: isPro ? (form.vat_number || "").trim() || undefined : undefined,
        is_pro: isPro,
      });
      toast.success("Inscription réussie. Bienvenue !");
      navigate("/compte", { replace: true });
    } catch (err) {
      const msg = err.message || "";
      if (msg.toLowerCase().includes("déjà enregistrée") || msg.toLowerCase().includes("deja enregistree")) {
        setProSiretBlocked(true);
        return;
      }
      toast.error(msg || "Inscription impossible.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "h-11 rounded-xl border-zinc-300 bg-white text-[15px] text-zinc-900 shadow-sm placeholder:text-zinc-400 focus-visible:border-[#190276] focus-visible:ring-2 focus-visible:ring-[#190276]/15";

  const renderBanDropdown = (open, list, onPick) =>
    open &&
    list.length > 0 && (
      <ul className="absolute z-20 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-zinc-200 bg-white py-1 shadow-lg">
        {list.map((s, i) => (
          <li key={`${s.label}-${i}`}>
            <button
              type="button"
              className="w-full px-3 py-2.5 text-left text-sm text-zinc-800 hover:bg-zinc-50"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(s)}
            >
              {s.label}
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <div className="min-h-screen w-full bg-zinc-100">
      <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-[2fr_1fr]">
        <aside className="relative order-2 flex flex-col overflow-hidden bg-gradient-to-br from-[#190276] via-[#1a0a6e] to-[#0c0438] text-white lg:order-1 lg:min-h-screen">
          <AuthPanelCornerDecor />
          <div
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 80% 50% at 100% 0%, rgba(242,181,49,0.22), transparent)",
            }}
            aria-hidden
          />
          <div className="relative z-[2] flex flex-col px-4 pb-8 pt-6 md:px-6 lg:min-h-screen lg:px-8 lg:pb-[calc(88px+1.25rem)] lg:pt-[calc(88px+1.25rem)] xl:px-10">
            <div className="hidden shrink-0 rounded-2xl border border-white/15 bg-[#0c0438]/65 px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.25)] backdrop-blur-md lg:block">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/90 drop-shadow-sm">
                Inscription
              </p>
              <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-white drop-shadow-sm xl:text-3xl">
                Rejoignez l&apos;espace client
              </h2>
              <p className="mt-6 max-w-xl text-base font-medium leading-relaxed text-white lg:text-lg">
                Créez votre compte pour commander et suivre vos dossiers.
              </p>
            </div>
            <AuthSidePromoSlot compact />
            <ul className="mt-6 space-y-2.5 lg:mt-10 lg:space-y-3">
              {PANEL_BULLETS.map(({ icon: Icon, text }) => (
                <li key={text} className="flex gap-2.5 text-xs text-white lg:gap-3 lg:text-sm">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/15 text-[#F2B531] shadow-sm lg:h-8 lg:w-8">
                    <Icon className="h-3.5 w-3.5 lg:h-4 lg:w-4" strokeWidth={2} />
                  </span>
                  <span className="leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 text-xs text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
              <span className="inline-block rounded-md bg-[#0c0438]/70 px-2 py-1 backdrop-blur-sm">
                © {new Date().getFullYear()} EPP
              </span>
            </p>
          </div>
        </aside>

        <div className="order-1 flex min-w-0 flex-col border-zinc-200/80 bg-zinc-100 lg:order-2 lg:border-l lg:bg-white">
          <div className="flex flex-1 flex-col px-4 pb-8 md:px-6 lg:justify-center lg:px-6 lg:py-12 xl:px-8">
            <div className="relative -mx-4 overflow-hidden bg-gradient-to-br from-[#190276] via-[#1a0a6e] to-[#0c0438] px-4 pb-8 pt-[max(0.75rem,env(safe-area-inset-top))] lg:hidden">
              <AuthMobileHeroAccents />
              <div className="relative z-[1] min-h-[4.5rem] pl-[3.25rem] text-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/75">Inscription</p>
                <h2 className="mt-2 font-heading text-lg font-bold leading-snug">
                  Rejoignez l&apos;espace client
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-white opacity-90">
                  Créez votre compte pour commander et suivre vos dossiers.
                </p>
              </div>
            </div>
            <div className="-mt-5 flex-1 rounded-t-[1.75rem] bg-white px-4 pb-8 pt-6 shadow-[0_-8px_32px_rgba(0,0,0,0.08)] ring-1 ring-zinc-200/90 lg:mt-0 lg:flex lg:flex-col lg:justify-center lg:rounded-none lg:bg-transparent lg:px-0 lg:pb-10 lg:pt-0 lg:shadow-none lg:ring-0">
            <div className="mx-auto w-full max-w-lg">
              {proSiretBlocked ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <div className="flex justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                      <Building2 className="h-7 w-7" strokeWidth={2} />
                    </span>
                  </div>
                  <div className="text-center">
                    <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
                      Entreprise déjà enregistrée
                    </h1>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                      Cette entreprise est déjà enregistrée chez nous. Veuillez contacter votre agence pour plus
                      d&apos;informations.
                    </p>
                  </div>
                  <Button
                    asChild
                    className="h-11 w-full rounded-xl bg-[#190276] font-semibold hover:bg-[#130160]"
                  >
                    <Link to="/">
                      <Home className="mr-2 h-4 w-4" strokeWidth={2} />
                      Retour à l&apos;accueil
                    </Link>
                  </Button>
                </motion.div>
              ) : (
                <>
              <div className="mb-2">
                <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">
                  Créer un compte
                </h1>
                <p className="mt-1 text-sm text-zinc-500">
                  Vous avez déjà un compte ?{" "}
                  <Link to="/connexion" className="font-semibold text-[#190276] hover:underline">
                    Connectez-vous
                  </Link>
                </p>
              </div>

              <Stepper current={step} steps={steps} />

              <form
                onSubmit={(e) => {
                  if (step !== 4) {
                    e.preventDefault();
                    return;
                  }
                  handleSubmit(e);
                }}
                className="space-y-5"
              >
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="s1"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <span className="mb-2 block text-sm font-medium text-zinc-700">
                          Civilité <span className="text-red-500">*</span>
                        </span>
                        <div className="flex gap-4">
                          <label className="flex cursor-pointer items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="civ"
                              checked={civility === "mr"}
                              onChange={() => setCivility("mr")}
                              className="text-[#190276]"
                            />
                            Monsieur
                          </label>
                          <label className="flex cursor-pointer items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="civ"
                              checked={civility === "mme"}
                              onChange={() => setCivility("mme")}
                              className="text-[#190276]"
                            />
                            Madame
                          </label>
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="ins-first" className="mb-1.5 block text-sm font-medium text-zinc-700">
                            Prénom <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                            <Input
                              id="ins-first"
                              placeholder="Jean"
                              value={form.first_name}
                              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                              className={cn(inputClass, "pl-10")}
                              autoComplete="given-name"
                            />
                          </div>
                        </div>
                        <div>
                          <label htmlFor="ins-last" className="mb-1.5 block text-sm font-medium text-zinc-700">
                            Nom de famille <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                            <Input
                              id="ins-last"
                              placeholder="Dupont"
                              value={form.last_name}
                              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                              className={cn(inputClass, "pl-10")}
                              autoComplete="family-name"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="ins-phone" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Numéro de téléphone <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Phone className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="ins-phone"
                            type="tel"
                            placeholder="6 01 64 27 76"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className={cn(inputClass, "pl-10")}
                            autoComplete="tel"
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-zinc-500">
                          Pour suivre vos commandes et sécuriser votre compte.
                        </p>
                      </div>

                      <div>
                        <label htmlFor="ins-email" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Adresse e-mail <span className="text-red-500">*</span>
                        </label>
                        {emailFromFlow && form.email ? (
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/90 px-4 py-3">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                                <Check className="h-4 w-4" strokeWidth={2.5} />
                              </span>
                              <span className="truncate text-sm font-medium text-emerald-950">{form.email}</span>
                            </div>
                            <Link
                              to={`/connexion?email=${encodeURIComponent(form.email)}`}
                              className="shrink-0 text-sm font-semibold text-[#190276] underline-offset-2 hover:underline"
                            >
                              Modifier
                            </Link>
                          </div>
                        ) : (
                          <div className="relative">
                            <Mail className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                            <Input
                              id="ins-email"
                              type="email"
                              placeholder="vous@exemple.fr"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              className={cn(inputClass, "pl-10")}
                              autoComplete="email"
                              required
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="ins-password" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Créer un mot de passe sécurisé <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="ins-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className={cn(inputClass, "pl-10 pr-11")}
                            autoComplete="new-password"
                            required
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#190276]/25"
                            aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          >
                            {showPassword ? (
                              <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
                            ) : (
                              <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
                            )}
                          </button>
                        </div>
                        <ul className="mt-3 space-y-1.5 text-xs text-zinc-600">
                          {[
                            { ok: checks.len, text: "8 à 128 caractères" },
                            { ok: checks.upper, text: "Majuscule" },
                            { ok: checks.lower, text: "Minuscule" },
                            { ok: checks.num, text: "Chiffre" },
                            { ok: checks.special, text: "Caractère spécial" },
                          ].map(({ ok, text }) => (
                            <li key={text} className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                  ok
                                    ? "border-emerald-500 bg-emerald-500 text-white"
                                    : "border-zinc-300 bg-white",
                                )}
                              >
                                {ok && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                              </span>
                              {text}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <span className="mb-2 block text-sm font-medium text-zinc-700">
                          Type de compte <span className="text-red-500">*</span>
                        </span>
                        <div className="flex rounded-xl border border-zinc-200 bg-zinc-50/80 p-1">
                          <button
                            type="button"
                            onClick={() => setIsPro(false)}
                            className={cn(
                              "flex-1 rounded-lg py-2.5 text-sm font-semibold transition-colors",
                              !isPro ? "bg-[#190276] text-white shadow-sm" : "text-zinc-600 hover:bg-white",
                            )}
                          >
                            Je suis un particulier
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsPro(true)}
                            className={cn(
                              "flex-1 rounded-lg py-2.5 text-sm font-semibold transition-colors",
                              isPro ? "bg-[#190276] text-white shadow-sm" : "text-zinc-600 hover:bg-white",
                            )}
                          >
                            Je suis un professionnel
                          </button>
                        </div>
                      </div>

                      {isPro && (
                        <>
                          <div>
                            <label htmlFor="ins-pro-siret-q" className="mb-1.5 block text-sm font-medium text-zinc-700">
                              Numéro de SIRET ou raison sociale <span className="text-red-500">*</span>
                            </label>
                            <Input
                              id="ins-pro-siret-q"
                              placeholder="Ex. 412 011 520 00027"
                              value={proCompanyQuery}
                              onChange={(e) => {
                                setProCompanyQuery(e.target.value);
                                setProCompanyPicked(false);
                              }}
                              className={inputClass}
                            />
                            <p className="mt-2 text-sm text-zinc-600">
                              <a
                                href={ANNUAIRE_ENTREPRISES}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 font-semibold text-[#190276] underline-offset-2 hover:underline"
                              >
                                Retrouvez votre SIRET via l&apos;Annuaire des Entreprises
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </p>
                          </div>
                          <div className="relative">
                            <label htmlFor="ins-pro-loc" className="mb-1.5 block text-sm font-medium text-zinc-700">
                              Code postal <span className="text-red-500">*</span>
                            </label>
                            <p className="mb-2 text-xs text-zinc-500">
                              Commencez à saisir pour voir des suggestions (ville, rue ou code postal).
                            </p>
                            <div className="relative">
                              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                              <Input
                                id="ins-pro-loc"
                                placeholder="Ex. 06210 ou Mandelieu"
                                value={proLocSearch}
                                onFocus={() => setProLocOpen(true)}
                                onChange={(e) => {
                                  setProLocOpen(true);
                                  setProLocSearch(e.target.value);
                                }}
                                onBlur={() => setTimeout(() => setProLocOpen(false), 200)}
                                className={cn(inputClass, "pl-10")}
                              />
                            </div>
                            {renderBanDropdown(proLocOpen, proLocSuggestions, pickProLocation)}
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Input
                                aria-label="Code postal (saisie directe)"
                                placeholder="CP"
                                value={proGeoPostal}
                                onChange={(e) => setProGeoPostal(e.target.value)}
                                className={cn(inputClass, "max-w-[120px]")}
                              />
                              <Input
                                aria-label="Ville"
                                placeholder="Ville"
                                value={proGeoCity}
                                onChange={(e) => setProGeoCity(e.target.value)}
                                className={cn(inputClass, "min-w-[160px] flex-1")}
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-200 bg-zinc-50/50 p-4">
                        <input
                          type="checkbox"
                          checked={acceptCgv}
                          onChange={(e) => setAcceptCgv(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-zinc-300 text-[#190276] focus:ring-[#190276]"
                        />
                        <span className="text-sm leading-snug text-zinc-700">
                          J&apos;accepte les{" "}
                          <Link to="/cgv" className="font-semibold text-[#190276] underline-offset-2 hover:underline">
                            conditions générales de vente
                          </Link>{" "}
                          <span className="text-red-500">*</span>
                        </span>
                      </label>

                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button type="button" variant="outline" className="h-11 rounded-xl border-zinc-300" asChild>
                          <Link to="/">Annuler</Link>
                        </Button>
                        <Button
                          type="button"
                          onClick={() => goNext(1)}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160]"
                        >
                          Continuer
                          <ArrowRight className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && isPro && (
                    <motion.div
                      key="s2pro"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-lg font-bold text-zinc-900">Trouver mon entreprise</h2>
                        <p className="mt-1 text-sm text-zinc-600">
                          Rien ne correspond ? Utilisez le{" "}
                          <a
                            href={ANNUAIRE_ENTREPRISES}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-[#190276] underline-offset-2 hover:underline"
                          >
                            service en ligne Annuaire des Entreprises
                          </a>
                          .
                        </p>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Numéro de SIRET <span className="text-red-500">*</span>
                        </label>
                        {proSiretLocked ? (
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                            <span className="font-mono text-sm font-medium text-zinc-900">{proCompanyQuery || "—"}</span>
                            <button
                              type="button"
                              className="text-sm font-semibold text-[#190276] hover:underline"
                              onClick={() => {
                                setProSiretLocked(false);
                                setProCompanyPicked(false);
                              }}
                            >
                              Modifier
                            </button>
                          </div>
                        ) : (
                          <Input
                            value={proCompanyQuery}
                            onChange={(e) => {
                              setProCompanyQuery(e.target.value);
                              setProCompanyPicked(false);
                            }}
                            className={inputClass}
                            placeholder="SIRET ou raison sociale"
                          />
                        )}
                      </div>

                      <p className="text-sm text-zinc-600">
                        Sélectionnez votre entreprise dans la liste ; si aucune ne correspond, modifiez la recherche.
                      </p>

                      {companyLoading && <p className="text-xs text-zinc-500">Recherche en cours…</p>}

                      {!companyLoading &&
                        proCompanyQuery.trim().length >= 2 &&
                        companyResults.length === 0 && (
                          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
                            Aucun résultat. Essayez une raison sociale ou un autre SIRET, ou vérifiez que le
                            frontend appelle le bon backend (<code className="rounded bg-white px-1">REACT_APP_BACKEND_URL</code>
                            ).
                          </p>
                        )}

                      <ul className="space-y-2">
                        {companyResults.map((row) => {
                          const rowSiretDigits = (row.siret || "").replace(/\D/g, "");
                          const picked =
                            proCompanyPicked && siretDigits.length === 14 && siretDigits === rowSiretDigits;
                          return (
                            <li key={row.siret}>
                              <button
                                type="button"
                                onClick={() => pickCompany(row)}
                                className={cn(
                                  "flex w-full items-start gap-3 rounded-xl border p-4 text-left transition",
                                  picked
                                    ? "border-[#190276] bg-[#190276]/5 ring-2 ring-[#190276]/20"
                                    : "border-zinc-200 bg-white hover:border-zinc-300",
                                )}
                              >
                                <span className="min-w-0 flex-1">
                                  <span className="block font-semibold text-zinc-900">{row.name}</span>
                                  <span className="mt-1 block text-sm text-zinc-600">{row.address}</span>
                                  <span className="mt-0.5 block text-sm text-zinc-600">
                                    {row.postal_code} {row.city}
                                  </span>
                                  <span className="mt-1 block font-mono text-xs text-zinc-500">
                                    SIRET : {row.siret_display || row.siret}
                                  </span>
                                </span>
                                {picked && (
                                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#190276] text-white">
                                    <Check className="h-4 w-4" strokeWidth={2.5} />
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>

                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-zinc-300"
                          onClick={() => {
                            setStep(1);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Retour
                        </Button>
                        <Button
                          type="button"
                          onClick={() => goNext(2)}
                          disabled={!step2ProValid}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160] disabled:opacity-50"
                        >
                          Continuer
                          <ArrowRight className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && !isPro && (
                    <motion.div
                      key="s2p"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-lg font-bold text-zinc-900">Adresse de facturation</h2>
                        <p className="mt-1 text-sm text-zinc-600">
                          Ces informations servent à vos factures et au choix de l&apos;agence.
                        </p>
                      </div>

                      <div className="relative">
                        <label htmlFor="ins-bill-q" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Recherche d&apos;adresse
                        </label>
                        <p className="mb-2 text-xs text-zinc-500">Au moins 3 caractères pour des suggestions.</p>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="ins-bill-q"
                            value={billAddrSearch}
                            onFocus={() => setBillAddrOpen(true)}
                            onChange={(e) => {
                              setBillAddrOpen(true);
                              setBillAddrSearch(e.target.value);
                            }}
                            onBlur={() => setTimeout(() => setBillAddrOpen(false), 200)}
                            className={cn(inputClass, "pl-10")}
                            placeholder="Rue ou code postal"
                          />
                        </div>
                        {renderBanDropdown(billAddrOpen, billAddrSuggestions, pickBillingAddress)}
                      </div>

                      <div>
                        <label htmlFor="ins-street-p" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Adresse <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="ins-street-p"
                          value={form.billing_street}
                          onChange={(e) => setForm({ ...form, billing_street: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label htmlFor="ins-pc-p" className="mb-1.5 block text-sm font-medium text-zinc-700">
                            Code postal <span className="text-red-500">*</span>
                          </label>
                          <Input
                            id="ins-pc-p"
                            value={form.billing_postal_code}
                            onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                        <div>
                          <label htmlFor="ins-city-p" className="mb-1.5 block text-sm font-medium text-zinc-700">
                            Ville <span className="text-red-500">*</span>
                          </label>
                          <Input
                            id="ins-city-p"
                            value={form.billing_city}
                            onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="ins-comp-p" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Complément (optionnel)
                        </label>
                        <Input
                          id="ins-comp-p"
                          value={form.billing_complement}
                          onChange={(e) => setForm({ ...form, billing_complement: e.target.value })}
                          className={inputClass}
                        />
                      </div>

                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-zinc-300"
                          onClick={() => setStep(1)}
                        >
                          Retour
                        </Button>
                        <Button
                          type="button"
                          onClick={() => goNext(2)}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160]"
                        >
                          Continuer
                          <ArrowRight className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && isPro && (
                    <motion.div
                      key="s3pro"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <h2 className="text-lg font-bold text-zinc-900">Détails de facturation</h2>

                      <div>
                        <label htmlFor="ins-co-name" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Raison sociale <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="ins-co-name"
                          value={form.company_name}
                          onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label htmlFor="ins-co-siret" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Numéro de SIRET <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="ins-co-siret"
                          value={form.siret}
                          onChange={(e) => setForm({ ...form, siret: e.target.value })}
                          className={cn(inputClass, "font-mono")}
                        />
                      </div>
                      <div>
                        <label htmlFor="ins-co-street" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Adresse <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="ins-co-street"
                          value={form.billing_street}
                          onChange={(e) => setForm({ ...form, billing_street: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div className="relative">
                        <label htmlFor="ins-co-cpville" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Code postal ou ville <span className="text-red-500">*</span>
                        </label>
                        <p className="mb-2 text-xs text-zinc-500">
                          Commencez à saisir du texte pour voir les suggestions.
                        </p>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="ins-co-cpville"
                            value={billAddrSearch}
                            onFocus={() => setBillAddrOpen(true)}
                            onChange={(e) => {
                              setBillAddrOpen(true);
                              setBillAddrSearch(e.target.value);
                            }}
                            onBlur={() => setTimeout(() => setBillAddrOpen(false), 200)}
                            className={cn(inputClass, "pl-10")}
                            placeholder="06210 — Mandelieu-la-Napoule"
                          />
                        </div>
                        {renderBanDropdown(billAddrOpen, billAddrSuggestions, pickBillingAddress)}
                        <div className="mt-2 grid gap-3 sm:grid-cols-2">
                          <Input
                            placeholder="Code postal"
                            value={form.billing_postal_code}
                            onChange={(e) => setForm({ ...form, billing_postal_code: e.target.value })}
                            className={inputClass}
                          />
                          <Input
                            placeholder="Ville"
                            value={form.billing_city}
                            onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                            className={inputClass}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="ins-vat-pro" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          N° TVA (optionnel)
                        </label>
                        <Input
                          id="ins-vat-pro"
                          value={form.vat_number}
                          onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                          className={inputClass}
                        />
                      </div>

                      <p className="text-sm text-zinc-600">
                        Vérifiez que toutes les informations de votre entreprise sont correctes avant de valider. Une
                        fois enregistrées, elles ne pourront être modifiées que sur demande auprès de votre agence.
                      </p>

                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button type="button" variant="outline" className="h-11 rounded-xl border-zinc-300" onClick={() => setStep(2)}>
                          Retour
                        </Button>
                        <Button
                          type="button"
                          onClick={() => goNext(3)}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160]"
                        >
                          Continuer
                          <ArrowRight className="h-4 w-4" strokeWidth={2} />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {(step === 3 && !isPro) || (step === 4 && isPro) ? (
                    <motion.div
                      key={`agency-${step}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-lg font-bold text-zinc-900">Choisir une agence</h2>
                        <p className="mt-1 text-sm text-zinc-600">
                          Ce choix permet d&apos;orienter stock et disponibilités ; vous pourrez le modifier plus tard
                          depuis votre compte lorsque c&apos;est possible.
                        </p>
                      </div>

                      <div className="relative">
                        <label htmlFor="ins-agency-loc" className="mb-1.5 block text-sm font-medium text-zinc-700">
                          Code postal, ville ou département <span className="text-red-500">*</span>
                        </label>
                        <p className="mb-2 text-xs text-zinc-500">
                          Commencez à saisir du texte pour voir les suggestions et affiner les distances.
                        </p>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-zinc-400" />
                          <Input
                            id="ins-agency-loc"
                            value={agencySearch}
                            onFocus={() => setAgencyOpen(true)}
                            onChange={(e) => {
                              setAgencyOpen(true);
                              setAgencySearch(e.target.value);
                            }}
                            onBlur={() => setTimeout(() => setAgencyOpen(false), 200)}
                            className={cn(inputClass, "pl-10")}
                            placeholder="06210 — Mandelieu-la-Napoule"
                          />
                        </div>
                        {renderBanDropdown(agencyOpen, agencySuggestions, pickAgencyLocation)}
                      </div>

                      {storesLoading ? (
                        <p className="text-sm text-zinc-500">Chargement des agences…</p>
                      ) : storesList.length === 0 ? (
                        <p className="text-sm text-amber-800">Aucun magasin disponible.</p>
                      ) : (
                        <ul className="space-y-2">
                          {storesList.map((st) => {
                            const sel = form.favorite_store_id === st.id;
                            const dist =
                              st.distance_km != null ? ` — ${st.distance_km} km` : "";
                            return (
                              <li key={st.id}>
                                <button
                                  type="button"
                                  onClick={() => setForm({ ...form, favorite_store_id: st.id })}
                                  className={cn(
                                    "flex w-full items-start gap-3 rounded-xl border p-4 text-left transition",
                                    sel
                                      ? "border-[#190276] bg-[#190276]/5 ring-2 ring-[#190276]/20"
                                      : "border-zinc-200 bg-white hover:border-zinc-300",
                                  )}
                                >
                                  <span
                                    className={cn(
                                      "mt-1 flex h-4 w-4 shrink-0 rounded-full border-2",
                                      sel ? "border-[#190276] bg-[#190276]" : "border-zinc-300",
                                    )}
                                    aria-hidden
                                  />
                                  <span className="min-w-0 flex-1">
                                    <span className="block font-semibold text-zinc-900">
                                      {st.name}
                                      {dist && <span className="font-normal text-zinc-500">{dist}</span>}
                                    </span>
                                    <span className="mt-0.5 block text-sm text-zinc-600">{st.address}</span>
                                    <span className="text-sm text-zinc-600">
                                      {st.postal_code} {st.city}
                                    </span>
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}

                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl border-zinc-300"
                          onClick={() => setStep(isPro ? 3 : 2)}
                        >
                          Retour
                        </Button>
                        {isPro ? (
                          <Button
                            type="submit"
                            disabled={loading || !submitProOk}
                            className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160] disabled:opacity-50"
                          >
                            {loading ? (
                              "Création…"
                            ) : (
                              <>
                                <UserPlus className="h-4 w-4" strokeWidth={2} />
                                Créer mon compte
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            onClick={() => goNext(3)}
                            disabled={!storeSelected}
                            className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160] disabled:opacity-50"
                          >
                            Continuer
                            <ArrowRight className="h-4 w-4" strokeWidth={2} />
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  ) : null}

                  {step === 4 && !isPro && (
                    <motion.div
                      key="s4p"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-5"
                    >
                      <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 text-sm text-zinc-700">
                        <p className="font-semibold text-zinc-900">Récapitulatif</p>
                        <ul className="mt-2 space-y-1">
                          <li>
                            {civility === "mme" ? "Mme" : "M."} {form.first_name} {form.last_name}
                          </li>
                          <li>{form.email}</li>
                          <li>{form.phone}</li>
                          <li>
                            {form.billing_street}, {form.billing_postal_code} {form.billing_city}
                          </li>
                          <li>Agence : {storesList.find((s) => s.id === form.favorite_store_id)?.name || "—"}</li>
                        </ul>
                      </div>
                      <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                        <Button type="button" variant="outline" className="h-11 rounded-xl border-zinc-300" onClick={() => setStep(3)}>
                          Retour
                        </Button>
                        <Button
                          type="submit"
                          disabled={loading || !submitPersoOk}
                          className="h-11 rounded-xl bg-[#190276] px-6 font-semibold hover:bg-[#130160]"
                        >
                          {loading ? (
                            "Création…"
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4" strokeWidth={2} />
                              Créer mon compte
                            </>
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  )}

                </AnimatePresence>
              </form>
                </>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
